import { useState } from 'react';
import { exportToXlsx } from '@/lib/utils/downloadXlsx';
import dayjs from 'dayjs';
import Image from 'next/image';
import ResultTable from './ResultTable';
import type { SearchResultItem } from './ResultTable';
import { formatNumber } from '@/lib/utils';
import AIUpgradeModal from './AIUpgradeModal';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { useTranslation } from 'next-i18next';

// 导出类型以便其他地方使用
export type { SearchResultItem };

// 搜索结果卡片类型
export interface SearchResultCard {
  id: string;
  title: string;
  subTitle: string;
  iconSrc: string;
  color: string;
}

// 搜索结果数据类型
export interface SearchResultData {
  intent?: string;
  insights?: string;
  tips?: string[];
  cards?: SearchResultCard[];
  results?: SearchResultItem[];
}

interface AiResultProps {
  data?: SearchResultData;
  onBack?: () => void;
  userCreditInfo?: {
    has_subscription?: boolean;
    expire_time?: string;
  };
}

export default function AiResult({ data, onBack, userCreditInfo }: AiResultProps) {
  const resultList = data?.results ?? [];
  const cardList = data?.cards || [];
  const intent = data?.intent ?? '';
  const insights = data?.insights ?? '';
  const tips = data?.tips ?? [];
  const { t } = useTranslation();

  // 升级弹窗状态
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // 判断是否有有效订阅
  const hasValidSubscription =
    userCreditInfo?.has_subscription &&
    (!userCreditInfo?.expire_time || new Date(userCreditInfo.expire_time) >= new Date());

  const handleExport = () => {
    ga_logEvent(EventName.ai_discover_export_click);
    // 判断是否订阅
    if (!hasValidSubscription) {
      // 未订阅，显示升级弹窗
      setShowUpgradeModal(true);
      return;
    }

    // 已订阅，执行导出
    exportToXlsx({
      filename: `instagram-ai-email-extraction-${dayjs().format('YYYY-MM-DD')}`,
      headers: ['#', 'Account', 'Categories', 'Followers', 'Following', 'Match Score', 'Public Email'],
      data: resultList.map((item, index) => [
        index + 1,
        item.username,
        item.tags.join(','),
        formatNumber(item.followerCount),
        formatNumber(item.followingCount),
        item.matchType === 1 ? t('High Match') : t('Medium Match'),
        item.email,
      ]),
    });
  };

  return (
    <div
      className="max-w-[1000px] lg:mx-auto mx-2 lg:mt-16 mt-6 bg-white rounded-2xl px-2 border border-[#E4E4E7]"
      style={{ boxShadow: '0px 4px 12px 0px #00000014' }}
    >
      {/* 描述文本区域 */}
      <div className="relative">
        {/* 返回按钮 */}
        <button
          className="absolute left-2 lg:left-4 top-4 border border-[#E4E4E7] rounded-lg px-4 py-2 hover:bg-[#d1d1d1] hover:text-white transition-all duration-300"
          onClick={onBack}
        >
          <span className="text-[#0C0C0C] group-hover:text-white text-sm font-normal leading-[1.33em] font-[Inter]">
            &lt; {t('back')}
          </span>
        </button>

        {/* 描述文本区域 */}
        <div className="max-w-[1000px] border-b w-full flex flex-col justify-center gap-4 pt-16 pb-8 lg:py-16 px-2 lg:px-4">
          <h1 className="text-3xl text-center font-bold">{t('Outreach Contact Results')}</h1>

          <div className="text-left w-full">
            <h2 className="text-xl font-bold">{t('Search Intent')}</h2>
            <p className="text-sm font-normal flex-wrap">{intent}</p>
          </div>

          <div className="w-full flex flex-col lg:flex-row gap-2 lg:gap-4 justify-center items-center">
            {cardList.map((item) => (
              <div key={item.id} className={`w-full lg:w-1/3 items-center gap-1 p-4 rounded-lg ${item.color}`}>
                <div className="flex flex-row gap-1 items-center mb-2">
                  <Image
                    src={item.iconSrc}
                    alt="verified"
                    width={16}
                    height={16}
                    className="inline-block size-4 object-cover"
                  />
                  <p className="inline-block text-[#0C0C0C] font-bold text-base leading-[1.21em] font-[Inter]">
                    {item.title}
                  </p>
                </div>
                <p className="text-[#0C0C0C] font-normal text-base leading-[1.21em] font-[Inter] opacity-60">
                  {item.subTitle}
                </p>
              </div>
            ))}
          </div>

          <div className="text-left w-full">
            <h2 className="text-xl font-bold">{t('Profile Insights')}</h2>
            <p className="text-sm font-normal flex-wrap">{insights}</p>
          </div>

          <div className="text-left w-full">
            <h2 className="text-xl font-bold">{t('Outreach Tips')}</h2>
            <div className="mt-2 space-y-2 text-sm">
              {tips.map((tip, index) => (
                <div
                  key={index}
                  className="relative pl-4 before:absolute before:left-0 before:top-1.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-black"
                >
                  {tip}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 导出按钮 */}
        <button
          className="hidden lg:flex absolute right-4 bottom-4 rounded-lg px-4 py-2 bg-[#725CFF] text-white transition-all duration-300"
          onClick={handleExport}
        >
          <Image
            className="inline-block size-4 object-cover"
            src="/images/instagram-email-finder/downloadwhite.svg"
            alt="download"
            width={16}
            height={16}
          />
          <span className="text-sm font-normal">{t('Export')}</span>
        </button>
      </div>

      {/* 结果表格 - PC端Table / 移动端列表 */}
      <ResultTable dataSource={resultList} userCreditInfo={userCreditInfo} />

      {/* 手机端导出按钮 */}
      <div className="lg:hidden w-full bg-white border-t p-2 fixed bottom-0 left-0 -translate-y-100% z-10">
        <button
          className="w-full rounded-lg py-2 bg-[#725CFF] text-white transition-all duration-300 flex gap-2 items-center justify-center"
          onClick={handleExport}
        >
          <Image
            className="inline-block size-4 object-cover"
            src="/images/instagram-email-finder/downloadwhite.svg"
            alt="download"
            width={16}
            height={16}
          />
          <span className="text-sm font-normal">{t('Export')}</span>
        </button>
      </div>

      {/* 升级弹窗 - Export */}
      <AIUpgradeModal
        open={showUpgradeModal}
        title="Upgrade Required"
        desc="Email exporting is a Premium-only feature. Please upgrade to export email results."
        btnTitle="Upgrade Now"
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          ga_logEvent(EventName.ai_discover_export_email_upgrade_click);
          document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
        }}
      />
    </div>
  );
}
